import React, { useState, useEffect, useRef } from "react";
import { AlertCircle, MessageSquare } from "lucide-react";
import {
  sendMessage,
  sendMediaMessage,
  checkWindowStatus,
  downloadMedia,
  uploadChatMedia,
  getChatAiStatus,
  toggleChatAiStatus,
  getFlowControlStatus,
  takeFlowControl,
  releaseFlowControl,
} from "../apis/chat";
import { useLiveChat } from "../contexts/LiveChatContext";
import PhoneSelector from "../components/PhoneSelector";
import ChatList from "../components/livechat/ChatList";
import ChatHeader from "../components/livechat/ChatHeader";
import MessageList from "../components/livechat/MessageList";
import MessageInput from "../components/livechat/MessageInput";
import SendTemplateModal from "../components/livechat/SendTemplateModal";
import ContactDetailsDrawer from "../components/contacts/ContactDetailsDrawer";
import ContactDrawer from "../components/contacts/ContactDrawer";
import { getContact } from "../apis/contacts";
import { splitWaId } from "../utils/contactDisplay";
import { validateMediaFile, MEDIA_KIND } from "../utils/whatsappMedia";
import { toast } from "react-toastify";

const LiveChats = () => {
  const {
    selectedChat,
    messages,
    loading,
    messagesLoading,
    messagesLoadingMore,
    selectChat,
    updateMessage,
    updateChatWindowStatus,
    updateChatInList,
    fetchMessages,
    chats,
    replyingTo,
    setReplyingTo,
    ingestRealtimeMessage,
  } = useLiveChat();

  const messageListRef = useRef(null);
  const livechatSection = "livechats";

  const [searchQuery, setSearchQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [isWindowOpen, setIsWindowOpen] = useState(true);
  const [windowWarning, setWindowWarning] = useState(false);
  const [downloadingMedia, setDownloadingMedia] = useState(new Set());
  const [chatAiEnabled, setChatAiEnabled] = useState(false);
  const [aiToggleLoading, setAiToggleLoading] = useState(false);
  const [agentHasControl, setAgentHasControl] = useState(false);
  const [flowControlLoading, setFlowControlLoading] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const messagesContainerRef = useRef(null);

  // Contact drawers driven from the chat header
  const [viewContactId, setViewContactId] = useState(null);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);

  const fetchChatAiStatus = async (customerPhone, phoneNumberId) => {
    try {
      const response = await getChatAiStatus(customerPhone, phoneNumberId);
      if (response.data.success) {
        setChatAiEnabled(response.data.data.ai_enabled || false);
      }
    } catch {
      setChatAiEnabled(false);
    }
  };

  const fetchFlowControlStatus = async (customerPhone, phoneNumberId) => {
    try {
      const response = await getFlowControlStatus(customerPhone, phoneNumberId);
      if (response.data.success) {
        setAgentHasControl(!!response.data.data.agent_has_control);
      }
    } catch {
      setAgentHasControl(false);
    }
  };

  const syncFlowControlToChatList = (customerPhone, phoneNumberId, hasControl) => {
    const chatId = `${customerPhone}_${phoneNumberId}`;
    updateChatInList(chatId, {
      agent_has_control: hasControl,
      flow_paused_by_agent: hasControl,
    });
  };

  const handleTakeFlowControl = async () => {
    if (!selectedChat || flowControlLoading) return;
    setFlowControlLoading(true);
    try {
      const response = await takeFlowControl(
        selectedChat.customer_phone,
        selectedChat.phone_number_id
      );
      if (response.data.success) {
        setAgentHasControl(true);
        syncFlowControlToChatList(
          selectedChat.customer_phone,
          selectedChat.phone_number_id,
          true
        );
        toast.success("You have taken control — flow paused");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to take control");
    } finally {
      setFlowControlLoading(false);
    }
  };

  const handleReleaseFlowControl = async () => {
    if (!selectedChat || flowControlLoading) return;
    setFlowControlLoading(true);
    try {
      const response = await releaseFlowControl(
        selectedChat.customer_phone,
        selectedChat.phone_number_id
      );
      if (response.data.success) {
        setAgentHasControl(false);
        syncFlowControlToChatList(
          selectedChat.customer_phone,
          selectedChat.phone_number_id,
          false
        );
        toast.success("Flow resumed from where it left off");
        await fetchMessages(selectedChat.customer_phone, selectedChat.phone_number_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to return control to flow");
    } finally {
      setFlowControlLoading(false);
    }
  };

  const handleToggleChatAi = async () => {
    if (!selectedChat || aiToggleLoading) return;
    const newStatus = !chatAiEnabled;
    setAiToggleLoading(true);
    try {
      const response = await toggleChatAiStatus(
        selectedChat.customer_phone,
        selectedChat.phone_number_id,
        newStatus
      );
      if (response.data.success) setChatAiEnabled(newStatus);
    } catch (error) {
      console.error("Error toggling chat AI status:", error);
    } finally {
      setAiToggleLoading(false);
    }
  };

  const handleChatSelect = async (chat) => {
    if (!chat.customer_phone || !chat.phone_number_id) return;
    setIsWindowOpen(chat.is_window_open !== undefined ? chat.is_window_open : true);
    selectChat(chat);
    setMobileChatOpen(true);
    setAgentHasControl(!!chat.agent_has_control);
    await fetchChatAiStatus(chat.customer_phone, chat.phone_number_id);
    await fetchFlowControlStatus(chat.customer_phone, chat.phone_number_id);
    await checkConversationWindow(chat.customer_phone, chat.phone_number_id);
    setTimeout(() => scrollToBottom(), 100);
  };

  const handleMobileBack = () => {
    setMobileChatOpen(false);
  };

  useEffect(() => {
    if (selectedChat?.agent_has_control !== undefined) {
      setAgentHasControl(!!selectedChat.agent_has_control);
    }
  }, [
    selectedChat?.agent_has_control,
    selectedChat?.customer_phone,
    selectedChat?.phone_number_id,
  ]);

  useEffect(() => {
    if (selectedChat) {
      const currentChat = chats.find(
        (c) =>
          c.customer_phone === selectedChat.customer_phone &&
          c.phone_number_id === selectedChat.phone_number_id
      );
      if (currentChat?.is_window_open) setIsWindowOpen(true);
      if (currentChat?.agent_has_control !== undefined) {
        setAgentHasControl(!!currentChat.agent_has_control);
      }
    }
  }, [selectedChat, chats]);

  const checkConversationWindow = async (customerPhone, phoneNumberId) => {
    try {
      const response = await checkWindowStatus(customerPhone, phoneNumberId);
      if (response.data.success) {
        const windowOpen = response.data.data.is_window_open;
        setIsWindowOpen(windowOpen);
        updateChatWindowStatus(customerPhone, phoneNumberId, windowOpen);
      }
    } catch (error) {
      console.error("Error checking window status:", error);
    }
  };

  /**
   * @param {Event} e — change event from the hidden <input type="file"/>
   * @param {string|null} expectedKind — when the user picked a slot
   *   (Photo / Video / Audio / Document), pass it so we reject mismatches.
   */
  const handleFileSelect = (e, expectedKind = null) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // Reset so re-selecting the same file fires onChange again.
    if (!file) return;

    const result = validateMediaFile(file, expectedKind);
    if (!result.ok) {
      toast.error(result.reason);
      return;
    }
    const kind = result.kind;

    setSelectedFile(file);
    if (kind === MEDIA_KIND.IMAGE) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview({ type: "image", url: ev.target.result });
      reader.readAsDataURL(file);
    } else if (kind === MEDIA_KIND.VIDEO) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview({ type: "video", url: ev.target.result });
      reader.readAsDataURL(file);
    } else if (kind === MEDIA_KIND.AUDIO) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview({ type: "audio", url: ev.target.result });
      reader.readAsDataURL(file);
    } else {
      setFilePreview({ type: "document", name: file.name, size: file.size });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!messageText.trim() && !selectedFile) || !selectedChat || sending || uploadingMedia) return;
    if (!agentHasControl) {
      toast.info("Take control first to reply manually");
      return;
    }
    if (!isWindowOpen) {
      setWindowWarning(true);
      setTimeout(() => setWindowWarning(false), 5000);
      return;
    }
    try {
      setSending(true);
      if (selectedFile) {
        setUploadingMedia(true);
        const uploadResponse = await uploadChatMedia(selectedFile);
        if (uploadResponse.data.success) {
          const response = await sendMediaMessage(
            selectedChat.phone_number_id,
            selectedChat.customer_phone,
            uploadResponse.data.data.media_url,
            uploadResponse.data.data.media_type,
            messageText.trim() || null,
            replyingTo?.message_id || null
          );
          if (response.data.success) {
            setMessageText("");
            setSelectedFile(null);
            setFilePreview(null);
            setReplyingTo(null);
            const message = response.data.data?.message;
            if (message) ingestRealtimeMessage(message);
          }
        }
        setUploadingMedia(false);
      } else {
        const response = await sendMessage(
          selectedChat.phone_number_id,
          selectedChat.customer_phone,
          messageText,
          replyingTo?.message_id || null
        );
        if (response.data.success) {
          setMessageText("");
          setReplyingTo(null);
          const message = response.data.data?.message;
          if (message) ingestRealtimeMessage(message);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      if (error.response?.data?.error === "CONVERSATION_WINDOW_CLOSED") {
        setWindowWarning(true);
        setIsWindowOpen(false);
        setTimeout(() => setWindowWarning(false), 5000);
      } else {
        alert(error.response?.data?.error || "Failed to send message. Please try again.");
      }
    } finally {
      setSending(false);
      setUploadingMedia(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const prevMessagesMetaRef = useRef({ length: 0, lastId: null, firstId: null });

  useEffect(() => {
    prevMessagesMetaRef.current = { length: 0, lastId: null, firstId: null };
  }, [selectedChat?.id]);

  useEffect(() => {
    if (messagesLoading || messagesLoadingMore || messages.length === 0) return;

    const lastId = messages[messages.length - 1]?.message_id || messages[messages.length - 1]?.id;
    const firstId = messages[0]?.message_id || messages[0]?.id;
    const prev = prevMessagesMetaRef.current;

    const appendedAtBottom =
      messages.length > prev.length && lastId !== prev.lastId;
    const initialLoad = prev.length === 0;

    if (initialLoad || appendedAtBottom) {
      scrollToBottom();
    }

    prevMessagesMetaRef.current = {
      length: messages.length,
      lastId,
      firstId,
    };
  }, [messages, messagesLoading, messagesLoadingMore]);

  const handleChatAssigned = (assignmentData) => {
    if (!assignmentData) return;
    const { customer_phone, phone_number_id } = assignmentData;
    const chatId = `${customer_phone}_${phone_number_id}`;
    updateChatInList(chatId, {
      assigned_agent_id: assignmentData.assigned_agent_id ?? null,
      assigned_agent_name: assignmentData.assigned_agent_name ?? null,
      assigned_at: assignmentData.assigned_at ?? null,
    });
    if (selectedChat && selectedChat.id === chatId) {
      selectChat({
        ...selectedChat,
        assigned_agent_id: assignmentData.assigned_agent_id ?? null,
        assigned_agent_name: assignmentData.assigned_agent_name ?? null,
        assigned_at: assignmentData.assigned_at ?? null,
      });
    }
  };

  /* ───────────────────────── Contact actions ───────────────────────── */

  const handleViewContact = () => {
    if (!selectedChat) return;
    if (selectedChat.contact_id) {
      setViewContactId(selectedChat.contact_id);
    } else {
      // No master row linked yet → fall back to the save flow.
      handleSaveContact();
    }
  };

  const handleSaveContact = async () => {
    if (!selectedChat) return;
    const phoneId = selectedChat.phone_number_id;

    // Prefer the WhatsApp profile name as the default name when saving.
    const profileName = selectedChat.customer_name || "";

    if (selectedChat.contact_id) {
      // The conversation already has an auto-synced master row — edit it so we
      // don't collide with the unique (account, phone_id, phone) key.
      try {
        const res = await getContact(selectedChat.contact_id, phoneId);
        const existing = res.data?.contact || {};
        const prefill = { ...existing };
        if (!existing.first_name && !existing.last_name && !existing.full_name && profileName) {
          prefill.first_name = profileName;
        }
        setContactToEdit(prefill);
        setContactDrawerOpen(true);
      } catch (err) {
        toast.error(err.response?.data?.error || "Failed to load contact");
      }
    } else {
      // Rare: master row not linked yet. Pre-split the WA id so the new
      // contact lines up with the eventual synced row.
      const { country_code, phone } = splitWaId(selectedChat.customer_phone);
      setContactToEdit({ phone, country_code, first_name: profileName });
      setContactDrawerOpen(true);
    }
  };

  const handleContactSaved = () => {
    setContactDrawerOpen(false);
    setContactToEdit(null);
    // Refresh the conversation so the saved name + is_saved_contact flag flow
    // into the header and sidebar immediately.
    if (selectedChat) {
      fetchMessages(selectedChat.customer_phone, selectedChat.phone_number_id);
    }
  };

  const handleDownloadMedia = async (message, isComplete = false) => {
    if (isComplete) {
      setDownloadingMedia((prev) => {
        const next = new Set(prev);
        next.delete(message.message_id);
        return next;
      });
      return;
    }
    if (!selectedChat || (!message.media_id && !message.media_url) || downloadingMedia.has(message.message_id)) return;

    // Private Saksham URLs without a signature always AccessDenied — re-sign via API.
    const needsResign =
      message.downloaded_media_url &&
      /object\.sakshamcloud\.com|speraza-marketplace-bucket|\/(whatsapp-media|chat-media)\//i.test(
        message.downloaded_media_url
      ) &&
      !/[?&]X-Amz-Signature=/i.test(message.downloaded_media_url);

    if (message.downloaded_media_url && !needsResign) return;
    try {
      setDownloadingMedia((prev) => new Set(prev).add(message.message_id));
      const response = await downloadMedia(message.message_id, selectedChat.phone_number_id);
      if (response.data.success && response.data.data.downloaded_media_url) {
        updateMessage(message.message_id, {
          downloaded_media_url: response.data.data.downloaded_media_url,
          canonical_url: response.data.data.canonical_url || null,
        });
        setDownloadingMedia((prev) => {
          const next = new Set(prev);
          next.delete(message.message_id);
          return next;
        });
      } else {
        alert(response.data?.error || "Failed to download media.");
        setDownloadingMedia((prev) => {
          const next = new Set(prev);
          next.delete(message.message_id);
          return next;
        });
      }
    } catch (error) {
      console.error("Error downloading media:", error);
      alert("Failed to download media. Please try again.");
      setDownloadingMedia((prev) => {
        const next = new Set(prev);
        next.delete(message.message_id);
        return next;
      });
    }
  };

  return (
    <div className="lc-root">
      {/* Window warning banner */}
      {windowWarning && (
        <div className="lc-warn-banner">
          <AlertCircle size={14} className="lc-warn-icon" />
          <span>
            Conversation window is closed. You can only send messages within 24 hours of the
            customer's last message. Please use a template message instead.
          </span>
        </div>
      )}

      {/* Main chat shell */}
      <div className="lc-shell">
        {/* Sidebar — hidden on mobile when chat is open */}
        <div className={`lc-sidebar${mobileChatOpen ? " lc-sidebar--hidden-mobile" : ""}`}>
          {/* Phone selector sits inside the sidebar */}
          <div className="lc-sidebar-phone">
            <PhoneSelector compact section={livechatSection} />
          </div>
          <ChatList
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onChatSelect={handleChatSelect}
            onChatsAssigned={handleChatAssigned}
          />
        </div>

        {/* Chat window — full-screen on mobile when open */}
        <div className={`lc-chat-window${mobileChatOpen ? " lc-chat-window--mobile-open" : ""}`}>
          {selectedChat ? (
            <>
              <ChatHeader
                selectedChat={selectedChat}
                isWindowOpen={isWindowOpen}
                chatAiEnabled={chatAiEnabled}
                aiToggleLoading={aiToggleLoading}
                onToggleChatAi={handleToggleChatAi}
                onChatAssigned={handleChatAssigned}
                onMobileBack={handleMobileBack}
                onViewContact={handleViewContact}
                onSaveContact={handleSaveContact}
              />
              <MessageList
                ref={messageListRef}
                downloadingMedia={downloadingMedia}
                onDownloadMedia={handleDownloadMedia}
                messagesContainerRef={messagesContainerRef}
                onStartReply={(msg) => setReplyingTo(msg)}
              />
              <MessageInput
                messageText={messageText}
                setMessageText={setMessageText}
                selectedFile={selectedFile}
                filePreview={filePreview}
                isWindowOpen={isWindowOpen}
                sending={sending}
                uploadingMedia={uploadingMedia}
                onFileSelect={handleFileSelect}
                onRemoveFile={handleRemoveFile}
                onSendMessage={handleSendMessage}
                replyingTo={replyingTo}
                onCancelReply={() => setReplyingTo(null)}
                onOpenTemplate={() => setTemplateModalOpen(true)}
                agentHasControl={agentHasControl}
                flowControlLoading={flowControlLoading}
                onTakeControl={handleTakeFlowControl}
                onReleaseFlowControl={handleReleaseFlowControl}
              />
            </>
          ) : (
            <div className="lc-empty-state">
              <div className="lc-empty-icon">
                <MessageSquare size={40} strokeWidth={1.2} />
              </div>
              <p className="lc-empty-title">Select a conversation</p>
              <p className="lc-empty-sub">Choose a chat from the sidebar to start messaging</p>
            </div>
          )}
        </div>
      </div>

      {/* Send template modal */}
      {selectedChat && (
        <SendTemplateModal
          isOpen={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
          phoneNumberId={selectedChat.phone_number_id}
          customerPhone={selectedChat.customer_phone}
          customerName={selectedChat.customer_name}
          onSent={(message) => {
            if (message) ingestRealtimeMessage(message);
            scrollToBottom();
            if (!isWindowOpen) {
              checkConversationWindow(
                selectedChat.customer_phone,
                selectedChat.phone_number_id
              );
            }
          }}
        />
      )}

      {/* View saved contact */}
      {viewContactId && selectedChat && (
        <ContactDetailsDrawer
          phoneId={selectedChat.phone_number_id}
          contactId={viewContactId}
          onClose={() => setViewContactId(null)}
          onEdit={async (c) => {
            setViewContactId(null);
            const prefill = { ...c };
            if (!c.first_name && !c.last_name && !c.full_name && selectedChat.customer_name) {
              prefill.first_name = selectedChat.customer_name;
            }
            setContactToEdit(prefill);
            setContactDrawerOpen(true);
          }}
          onDeleted={() => {
            setViewContactId(null);
            if (selectedChat) fetchMessages(selectedChat.customer_phone, selectedChat.phone_number_id);
          }}
        />
      )}

      {/* Create / edit (save) contact */}
      {contactDrawerOpen && selectedChat && (
        <ContactDrawer
          phoneId={selectedChat.phone_number_id}
          contact={contactToEdit}
          onClose={() => { setContactDrawerOpen(false); setContactToEdit(null); }}
          onSaved={handleContactSaved}
        />
      )}

      <style>{`
        /* ── Design tokens ───────────────────────── */
        .lc-root {
          --lc-green: #25D366;
          --lc-green-dk: #128C7E;
          --lc-teal: #075E54;
          --lc-surface: #f0f2f5;
          --lc-chat-bg: #efeae2;
          --lc-border: rgba(0,0,0,0.09);
          --lc-text: #111b21;
          --lc-text-2: #54656f;
          --lc-text-3: #8696a0;
          --lc-header: #f0f2f5;
          --lc-radius: 4px;
          --lc-radius-lg: 6px;
          --lc-sidebar-w: 320px;
          --lc-app-header-h: 64px;            /* desktop fixed app header (h-16) */
          --lc-pad: 12px;                      /* outer breathing room */
          /* Fill exactly the viewport area below the app's fixed header */
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          height: calc(100vh - var(--lc-app-header-h));
          background: var(--lc-surface);
          display: flex;
          flex-direction: column;
          padding: var(--lc-pad);
          overflow-x: hidden;
          overflow-y: hidden;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }

        /* ── Sidebar phone selector ──────────────── */
        .lc-sidebar-phone {
          padding: 10px 12px;
          border-bottom: 0.5px solid var(--lc-border);
          background: #fff;
          flex-shrink: 0;
        }

        /* ── Warning banner ──────────────────────── */
        .lc-warn-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: #fffbeb;
          border: 1px solid #fcd34d;
          border-radius: var(--lc-radius-lg);
          margin-bottom: var(--lc-pad);
          font-size: 12px;
          color: #92400e;
          flex-shrink: 0;
        }
        .lc-warn-icon { color: #d97706; flex-shrink: 0; }

        /* ── Shell ───────────────────────────────── */
        .lc-shell {
          flex: 1;
          display: flex;
          overflow: hidden;
          background: #fff;
          border: 0.5px solid var(--lc-border);
          border-radius: var(--lc-radius-lg);
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          min-height: 0;
          min-width: 0;
          max-width: 100%;
        }

        /* ── Sidebar ─────────────────────────────── */
        .lc-sidebar {
          width: var(--lc-sidebar-w);
          min-width: var(--lc-sidebar-w);
          display: flex;
          flex-direction: column;
          border-right: 0.5px solid var(--lc-border);
          background: #fff;
          overflow: hidden;
          min-height: 0;
          border-top-left-radius: var(--lc-radius-lg);
          border-bottom-left-radius: var(--lc-radius-lg);
        }

        /* ── Chat window ─────────────────────────── */
        .lc-chat-window {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: var(--lc-chat-bg);
          overflow: hidden;
          position: relative;
          min-height: 0;
          min-width: 0;
          max-width: 100%;
          border-top-right-radius: var(--lc-radius-lg);
          border-bottom-right-radius: var(--lc-radius-lg);
        }

        /* ── Empty state ─────────────────────────── */
        .lc-empty-state {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: var(--lc-chat-bg);
        }
        .lc-empty-icon {
          width: 72px;
          height: 72px;
          border-radius: var(--lc-radius);
          background: rgba(0,0,0,0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(0,0,0,0.25);
        }
        .lc-empty-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--lc-text-2);
        }
        .lc-empty-sub {
          font-size: 12.5px;
          color: var(--lc-text-3);
        }

        /* ── Mobile responsive (WhatsApp-style slide-in chat) ── */
        @media (max-width: 767px) {
          .lc-root {
            --lc-app-header-h: 56px;          /* mobile app header (h-14) */
            --lc-pad: 0px;                     /* edge-to-edge on mobile */
          }
          .lc-shell {
            position: relative;
            border-radius: 0;
            border-left: 0;
            border-right: 0;
            box-shadow: none;
          }
          .lc-sidebar {
            border-radius: 0;
            width: 100%;
            min-width: 0;
            flex: 1;
          }
          .lc-chat-window {
            border-radius: 0;
          }
          .lc-warn-banner { border-radius: 0; margin-bottom: 0; }

          /* Chat list hides without layout jump (keeps slide animation smooth). */
          .lc-sidebar--hidden-mobile {
            visibility: hidden;
            position: absolute;
            inset: 0;
            opacity: 0;
            pointer-events: none;
            z-index: 0;
          }

          .lc-chat-window {
            position: absolute;
            inset: 0;
            z-index: 20;
            display: flex;
            flex-direction: column;
            transform: translateX(100%);
            transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
            will-change: transform;
            box-shadow: -6px 0 28px rgba(0, 0, 0, 0.1);
            pointer-events: none;
          }
          .lc-chat-window--mobile-open {
            transform: translateX(0);
            pointer-events: auto;
          }
        }

        @media (max-width: 767px) and (prefers-reduced-motion: reduce) {
          .lc-chat-window {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

export default LiveChats;